import {
  flatten,
  get,
  isEqualWith,
  isFunction,
  map,
  partial,
  reduce,
  uniq,
  isEmpty,
} from "lodash";
import { Component, h } from "preact";
import { ApiIO, getItemsByIds, getItemsByTypeAndIds, search } from "common/api";
import { AccountTagPicker } from "common/component/AccountTagPicker";
import { toGetCriteria, toSearchCriteria } from "common/criteria/account";
import { minimumInputCharSearch } from "common/picker/constant";
import {
  initState,
  onBlur,
  onError,
  onFocus,
  onInput,
  onRemove,
  onSearch,
  onSelect,
  onStart,
  ReducerIO,
  reinitState,
} from "common/picker/reducer";
import { State } from "common/picker/reducer/types";
import {
  ClientDto,
  SearchCriteria,
  searchProperties,
} from "common/types/account";
import { FilterKeyMapper, IO } from "../types";

export class Main extends Component<
  IO<ClientDto>,
  State<ClientDto, SearchCriteria>
> {
  public state: any;
  private apiIO: any;
  private searchById: boolean = false;

  constructor(props: IO<ClientDto>) {
    super(props);

    this.searchById =
      !props.selectedSearchBy || props.selectedSearchBy === "id";
    this.apiIO = {
      baseUrl: (id: string) =>
        `2.0/clients` + (this.searchById && id ? `/${id}` : ``),
      fetch: props.fetch,
      headers: props.headers,
      serviceUrl: props.soaUrl || "",
      ssoV2: props.ssoV2,
      type: "json",
      bus: props.bus,
      env: props.env,
    };

    const reducerIO: ReducerIO = {
      capacity: props.capacity,
      displaySearchBy: props.displaySearchBy,
      placeholder: props.placeholder ? props.placeholder : "Search account",
      selectedSearchBy: props.selectedSearchBy,
      state: props.state,
      userEmail: props.userEmail,
    };
    const tagList = {
      disabled: reducerIO.capacity <= 0 || reducerIO.disabled || false,
      displaySearchBy: reducerIO.displaySearchBy || true,
      hasPortfolio: reducerIO.userEmail !== "",
      placeholder: reducerIO.placeholder,
      selectedSearchBy: reducerIO.selectedSearchBy || "",
      state: reducerIO.state || "empty",
      status: "init",
      tags: [],
    };
    const autocomplete = {
      exclude: [],
      isVisible: false,
      items: [],
      reason: "",
      status: "init",
    };
    this.state = {
      autocomplete,
      capacity: reducerIO.capacity,
      criteria: undefined,
      tagList,
      terms: "",
    };
  }

  public componentDidMount(): void {
    this.initialize(this.props, true);
    // emit only when empty
    if (
      this.props.emitOnMount &&
      isEmpty(this.props.readonlyIds) &&
      isEmpty(this.props.selectedIds)
    ) {
      this.props.onChange([]);
    }
  }

  public initialize(io: IO<ClientDto>, init: boolean) {
    const reducerIO: ReducerIO = {
      capacity: io.capacity,
      displaySearchBy: io.displaySearchBy,
      placeholder: io.placeholder ? io.placeholder : "Search account",
      selectedSearchBy: io.selectedSearchBy,
      state: io.state,
      userEmail: io.userEmail,
    };

    this.setState((state: State<ClientDto, SearchCriteria>) => {
      const initialState = init
        ? initState<ClientDto, SearchCriteria>(reducerIO)
        : reinitState<ClientDto, SearchCriteria>(reducerIO, state);
      this.setState(initialState);
      this.onStart(io);
    });
  }

  public componentWillReceiveProps(nextProps: IO<ClientDto>) {
    if (!isEqualWith(this.props, nextProps, this.compareWithoutFunctions)) {
      this.initialize(nextProps, false);
    }
  }

  public render() {
    return (
      <AccountTagPicker
        maxItemNameLength={this.props.maxItemNameLength}
        capacity={this.state.capacity}
        autocomplete={this.state.autocomplete}
        criteria={this.state.criteria!}
        onBlur={() =>
          this.setState((state: State<ClientDto, SearchCriteria>) =>
            onBlur(state)
          )
        }
        onFocus={() =>
          this.setState((state: State<ClientDto, SearchCriteria>) =>
            onFocus(state)
          )
        }
        onInput={terms => this.onInput(terms, this.props)}
        onRemove={item => this.checkForChanges(state => onRemove(state, item))}
        onSelect={item => this.checkForChanges(state => onSelect(state, item))}
        onStart={() => this.onStart(this.props)}
        tagList={this.state.tagList}
        terms={this.state.terms}
        hostName="IC-ACCOUNT-PICKER"
      />
    );
  }

  protected compareWithoutFunctions = (a: any, b: any) =>
    isFunction(a) && isFunction(b) ? true : undefined;

  protected checkForChanges(
    reducer: (
      state: State<ClientDto, SearchCriteria>
    ) => State<ClientDto, SearchCriteria>
  ) {
    let prev = 0;
    this.setState(
      (state: State<ClientDto, SearchCriteria>) => {
        prev = state.tagList.tags.length;
        return reducer(state);
      },
      () => {
        const tags = this.state.tagList.tags;
        if (prev !== tags.length) {
          this.props.onChange(tags);
        }
      }
    );
  }

  protected onInput(terms: string, io: IO<ClientDto>) {
    const criteria = toSearchCriteria(io.userEmail, terms);
    this.setState((state: State<ClientDto, SearchCriteria>) =>
      onInput(state, terms, criteria)
    );

    if (terms.length < minimumInputCharSearch) {
      return Promise.resolve([]).then(items =>
        this.setState((state: State<ClientDto, SearchCriteria>) =>
          onSearch(state, items)
        )
      );
    }

    const filterItems = (clients: ClientDto[]): ClientDto[] =>
      clients.filter(client =>
        reduce(
          io.filter,
          (acc: boolean, values: Array<string | number>, key: string) =>
            acc &&
            values.indexOf(
              get(client, FilterKeyMapper[key]) as string | number
            ) >= 0,
          true
        )
      );

    return search<ClientDto[]>(this.apiIO)(criteria)
      .then(items => (io.filter ? filterItems(items) : items))
      .then(items =>
        this.setState((state: State<ClientDto, SearchCriteria>) =>
          onSearch(state, items)
        )
      )
      .catch((reason: Error) =>
        this.setState((state: State<ClientDto, SearchCriteria>) =>
          onError(state, reason.message)
        )
      );
  }

  protected onStart(io: IO<ClientDto>) {
    const getAccountsByIds = this.searchById
      ? getItemsByIds<ClientDto>(this.apiIO)
      : getItemsByTypeAndIds<ClientDto, SearchCriteria>(
          this.apiIO,
          io.selectedSearchBy,
          partial(toSearchCriteria, io.userEmail)
        );

    const readOnlyItems = getAccountsByIds(
      toGetCriteria(uniq(io.readonlyIds), searchProperties)
    ).then((items: ClientDto[] | ClientDto[][]) =>
      map(flatten(items), i => ({ ...i, readonly: true }))
    );

    const selectedItems = getAccountsByIds(
      toGetCriteria(uniq(io.selectedIds), searchProperties)
    ).then((items: ClientDto[] | ClientDto[][]) => flatten(items));

    const itemsPromise = Promise.all([readOnlyItems, selectedItems]);

    return itemsPromise
      .then(items =>
        this.checkForChanges(state => onStart(state, flatten(items), io))
      )
      .catch((reason: Error) =>
        this.setState((state: State<ClientDto, SearchCriteria>) =>
          onError(state, reason.message)
        )
      );
  }
}
